import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LayoutGrid, List } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { FileUpload } from "./FileUpload";
import { PageHeader, DataTable } from "./bits";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const NONE = "_none";

export function CrudPage({
  title, subtitle, endpoint, columns, fields, testid,
  canWrite = true, canDelete = false, base, listQuery = "", transformPayload, onRowClick,
  enableViewToggle = false, cardRender, headerExtra,
}) {
  const [items, setItems] = useState([]);
  const [viewMode, setViewMode] = useState(localStorage.getItem(`crudview-${testid}`) || "table");

  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem(`crudview-${testid}`, mode);
  };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [apiOptions, setApiOptions] = useState({});
  const basePath = base || `/r/${endpoint}`;

  const load = useCallback(() => {
    api.get(`${basePath}${listQuery}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [basePath, listQuery, formatApiError]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fields.filter((f) => f.type === "select-api" || f.type === "multi-api").forEach((f) => {
      api.get(`/${f.source}`).then((r) => setApiOptions((prev) => ({ ...prev, [f.key]: r.data }))).catch(() => {});
    });
  }, [fields]);

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => {
      init[f.key] = f.type === "switch" ? (f.default ?? true) : f.type === "multi-api" ? [] : "";
    });
    setEditing(null);
    setForm(init);
    setOpen(true);
  };

  const openEdit = (item) => {
    const init = {};
    fields.forEach((f) => {
      init[f.key] = item[f.key] ?? (f.type === "switch" ? true : f.type === "multi-api" ? [] : "");
    });
    setEditing(item);
    setForm(init);
    setOpen(true);
  };

  const submit = async () => {
    try {
      let payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number") payload[f.key] = payload[f.key] === "" ? 0 : parseFloat(payload[f.key]);
        if (f.type === "select" || f.type === "select-api") {
          if (payload[f.key] === NONE) payload[f.key] = "";
        }
      });
      if (transformPayload) payload = transformPayload(payload);
      if (editing) {
        await api.put(`${basePath}/${editing.id}`, payload);
        toast.success("Registro actualizado");
      } else {
        await api.post(basePath, payload);
        toast.success("Registro creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.delete(`${basePath}/${item.id}`);
      toast.success("Registro eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const actionCol = {
    key: "_actions", label: "Acciones",
    render: (row) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        {canWrite && (
          <Button size="sm" variant="ghost" data-testid={`edit-${testid}-${row.id}`} onClick={() => openEdit(row)}>
            <Pencil className="w-4 h-4" />
          </Button>
        )}
        {canDelete && (
          <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`delete-${testid}-${row.id}`} onClick={() => remove(row)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    ),
  };

  const cols = canWrite || canDelete ? [...columns, actionCol] : columns;

  return (
    <div data-testid={`${testid}-page`}>
      <PageHeader title={title} subtitle={subtitle}>
        {headerExtra}
        {enableViewToggle && cardRender && (
          <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid={`${testid}-view-toggle`}>
            <button
              data-testid={`${testid}-view-table`}
              onClick={() => switchView("table")}
              className={`px-3 py-2 text-sm transition-colors ${viewMode === "table" ? "bg-blue-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              title="Vista de tabla"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              data-testid={`${testid}-view-cards`}
              onClick={() => switchView("cards")}
              className={`px-3 py-2 text-sm transition-colors ${viewMode === "cards" ? "bg-blue-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              title="Vista de tarjetas"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        )}
        {canWrite && (
          <Button data-testid={`create-${testid}-button`} onClick={openCreate} className="bg-blue-800 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-1" /> Nuevo
          </Button>
        )}
      </PageHeader>
      {viewMode === "cards" && cardRender ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testid}-cards`}>
          {items.map((row) => cardRender(row, actionCol.render(row)))}
          {items.length === 0 && <p className="text-slate-400 text-sm col-span-full">Sin registros</p>}
        </div>
      ) : (
        <DataTable columns={cols} rows={items} testid={`${testid}-table`} onRowClick={onRowClick} searchable searchPlaceholder="Buscar en esta lista…" />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar" : "Crear"} — {title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                {f.type !== "switch" && <Label>{f.label}</Label>}
                {(f.type === "text" || f.type === "email") && (
                  <Input data-testid={`field-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                )}
                {f.type === "number" && (
                  <Input data-testid={`field-${f.key}`} type="number" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                )}
                {f.type === "date" && (
                  <Input data-testid={`field-${f.key}`} type="date" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                )}
                {f.type === "textarea" && (
                  <Textarea data-testid={`field-${f.key}`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                )}
                {f.type === "select" && (
                  <Select value={form[f.key] === "" ? NONE : String(form[f.key])} onValueChange={(v) => setForm({ ...form, [f.key]: v === NONE ? "" : v })}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {!f.required && <SelectItem value={NONE}>—</SelectItem>}
                      {f.options.map((o) => (
                        <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {f.type === "select-api" && (
                  <Select value={form[f.key] === "" ? NONE : String(form[f.key])} onValueChange={(v) => setForm({ ...form, [f.key]: v === NONE ? "" : v })}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {!f.required && <SelectItem value={NONE}>—</SelectItem>}
                      {(apiOptions[f.key] || []).map((o) => (
                        <SelectItem key={o.id} value={o.id}>{f.optionLabel ? f.optionLabel(o) : o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {f.type === "multi-api" && (
                  <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2" data-testid={`field-${f.key}`}>
                    {(apiOptions[f.key] || []).map((o) => {
                      const checked = (form[f.key] || []).includes(o.id);
                      return (
                        <label key={o.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const cur = form[f.key] || [];
                              setForm({ ...form, [f.key]: checked ? cur.filter((x) => x !== o.id) : [...cur, o.id] });
                            }}
                          />
                          {f.optionLabel ? f.optionLabel(o) : o.name}
                        </label>
                      );
                    })}
                    {(apiOptions[f.key] || []).length === 0 && <p className="text-xs text-slate-400">Sin opciones disponibles</p>}
                  </div>
                )}
                {f.type === "file" && (
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                      <Input data-testid={`field-${f.key}`} placeholder="https://… o sube un archivo" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                      <FileUpload
                        category={f.category || "general"} accept={f.accept} testid={`field-${f.key}-upload`} label="Subir"
                        onUploaded={(file) => setForm((prev) => ({ ...prev, [f.key]: file.url }))}
                      />
                    </div>
                    {form[f.key] && (
                      <a href={form[f.key]} target="_blank" rel="noreferrer" className="text-xs text-sky-600 underline" data-testid={`field-${f.key}-preview`}>
                        Ver archivo actual
                      </a>
                    )}
                  </div>
                )}
                {f.type === "file" && (
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                      <Input data-testid={`field-${f.key}`} placeholder="https://… o sube un archivo" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                      <FileUpload
                        category={f.category || "general"} accept={f.accept} testid={`field-${f.key}-upload`} label="Subir"
                        onUploaded={(file) => setForm((prev) => ({ ...prev, [f.key]: file.url }))}
                      />
                    </div>
                    {form[f.key] && (
                      <a href={form[f.key]} target="_blank" rel="noreferrer" className="text-xs text-sky-600 underline" data-testid={`field-${f.key}-preview`}>
                        Ver archivo actual
                      </a>
                    )}
                  </div>
                )}
                {f.type === "switch" && (
                  <div className="flex items-center gap-3">
                    <Switch data-testid={`field-${f.key}`} checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} />
                    <Label className="!mt-0">{f.label}</Label>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" data-testid={`cancel-${testid}`} onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid={`submit-${testid}`} onClick={submit} className="bg-blue-800 hover:bg-blue-700">
              {editing ? "Guardar cambios" : "Crear"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
